/**
 * Persist recently viewed product handles/ids in localStorage.
 * Liquid renders product id/handle on <body>; this script hydrates
 * storage after paint so it stays safe in non-browser/SSR contexts.
 * Keyboard: Enter/Space store the focused product link; Arrow/Home/End
 * move focus between recently viewed product links.
 */
(function () {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  var KEY = 'furbish-recently-viewed';
  var MAX = 12;

  function canUseStorage() {
    try {
      return Boolean(window.localStorage);
    } catch (error) {
      return false;
    }
  }

  function normalizeHandle(handle) {
    if (!handle) return '';
    return String(handle)
      .split('?')[0]
      .split('#')[0]
      .replace(/\/+$/, '')
      .replace(/^\/+/, '');
  }

  function store(id, handle) {
    handle = normalizeHandle(handle);
    id = id ? String(id) : '';
    if (!id && !handle) return;
    if (!canUseStorage()) return;

    try {
      var items = JSON.parse(localStorage.getItem(KEY) || '[]');
      if (!Array.isArray(items)) items = [];

      items = items.filter(function (item) {
        if (!item) return false;
        var sameId = id && String(item.id) === id;
        var sameHandle = handle && item.handle === handle;
        return !sameId && !sameHandle;
      });

      items.unshift({ id: id, handle: handle });
      localStorage.setItem(KEY, JSON.stringify(items.slice(0, MAX)));
    } catch (error) {}
  }

  window.__storeRecentlyViewed = store;

  function drainQueue() {
    var queue = window.__recentlyViewedQueue;
    if (!queue || !queue.length) return;
    window.__recentlyViewedQueue = [];
    for (var i = 0; i < queue.length; i++) {
      var item = queue[i];
      if (item) store(item[0], item[1]);
    }
  }

  function storeFromPage() {
    var body = document.body;
    if (!body) return;
    store(body.getAttribute('data-recently-viewed-id'), body.getAttribute('data-recently-viewed-handle'));
    drainQueue();
  }

  function scheduleStoreFromPage() {
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(storeFromPage, { timeout: 2000 });
      return;
    }
    window.setTimeout(storeFromPage, 0);
  }

  function getProductLink(event) {
    var target = event.target;
    if (!target || typeof target.closest !== 'function') return null;
    if (target.closest('[disabled], [aria-disabled="true"]')) return null;
    return target.closest('a[href*="/products/"]');
  }

  function storeFromProductLink(link) {
    if (!link) return;
    try {
      var url = new URL(link.href, window.location.origin);
      var match = url.pathname.match(/\/products\/([^\/]+)/);
      if (match) store('', decodeURIComponent(match[1]));
    } catch (error) {}
  }

  function getRecentlyViewedLinks(section) {
    var slides = section.querySelectorAll('.recently-viewed__slide');
    var links = [];
    Array.prototype.forEach.call(slides, function (slide) {
      var link =
        slide.querySelector('.lp-collection-card-v2__title a') ||
        slide.querySelector('a[href*="/products/"]:not([tabindex="-1"])');
      if (link) links.push(link);
    });
    return links;
  }

  function formatAriaTemplate(template, position, count, name) {
    return String(template || '')
      .replace(/\[position\]/g, String(position))
      .replace(/\[count\]/g, String(count))
      .replace(/\[name\]/g, name || '');
  }

  function ensureAriaLabelledBy(section) {
    var headingId = section.getAttribute('data-heading-id') || 'rvh-recently-viewed';
    var heading = section.querySelector('.recently-viewed__heading');
    if (!heading) {
      heading = document.createElement('h2');
      heading.className = 'recently-viewed__heading visually-hidden';
      heading.tabIndex = 0;
      var inner = section.querySelector('.recently-viewed__inner') || section;
      inner.insertBefore(heading, inner.firstChild);
    }
    heading.id = headingId;
    if (!heading.textContent.trim()) {
      heading.textContent = section.getAttribute('data-heading-label') || 'Recently viewed products';
    }
    if (heading.classList.contains('visually-hidden')) {
      heading.tabIndex = 0;
    }
    section.setAttribute('aria-labelledby', heading.id);
  }

  function ensureAriaDescribedBy(section) {
    var descriptionId = section.getAttribute('data-description-id') || 'rvd-recently-viewed';
    var description =
      section.querySelector('[data-recently-viewed-description]') || document.getElementById(descriptionId);
    if (!description) {
      description = document.createElement('p');
      description.className = 'visually-hidden';
      description.setAttribute('data-recently-viewed-description', '');
      var heading = section.querySelector('.recently-viewed__heading');
      if (heading && heading.parentNode) {
        heading.insertAdjacentElement('afterend', description);
      } else {
        section.appendChild(description);
      }
    }
    description.id = descriptionId;
    if (!description.textContent.trim()) {
      description.textContent =
        section.getAttribute('data-carousel-description') ||
        'Carousel of products you recently viewed on this store. Use arrow keys or the previous and next buttons to move between product cards.';
    }
    section.setAttribute('aria-describedby', description.id);
  }

  function syncRecentlyViewedAria(section, focusedLink) {
    if (!section) return;

    section.setAttribute('role', 'region');
    section.setAttribute('aria-roledescription', 'carousel');
    section.setAttribute('aria-keyshortcuts', 'ArrowLeft ArrowRight Home End');
    ensureAriaLabelledBy(section);
    ensureAriaDescribedBy(section);

    var live = section.querySelector('[data-recently-viewed-live]');
    if (!live) {
      live = document.createElement('div');
      live.className = 'visually-hidden';
      live.setAttribute('data-recently-viewed-live', '');
      live.setAttribute('role', 'status');
      live.setAttribute('aria-live', 'polite');
      live.setAttribute('aria-atomic', 'true');
      section.appendChild(live);
    }

    var links = getRecentlyViewedLinks(section);
    var count = links.length;
    var slideTemplate = section.getAttribute('data-slide-label') || 'Slide [position] of [count]: [name]';
    var liveTemplate = section.getAttribute('data-live-status') || 'Product [position] of [count]: [name]';

    Array.prototype.forEach.call(links, function (link, index) {
      var position = index + 1;
      var name = (link.textContent || '').trim();
      var slide = link.closest('.recently-viewed__slide');
      var label = formatAriaTemplate(slideTemplate, position, count, name);

      if (slide) {
        slide.setAttribute('role', 'group');
        slide.setAttribute('aria-roledescription', 'slide');
        slide.setAttribute('aria-label', label);
        slide.setAttribute('aria-setsize', String(count));
        slide.setAttribute('aria-posinset', String(position));
      }

      link.setAttribute('aria-setsize', String(count));
      link.setAttribute('aria-posinset', String(position));

      if (link === focusedLink) {
        link.setAttribute('aria-current', 'true');
      } else {
        link.removeAttribute('aria-current');
      }
    });

    if (focusedLink && live) {
      var focusedIndex = Array.prototype.indexOf.call(links, focusedLink) + 1;
      live.textContent = formatAriaTemplate(
        liveTemplate,
        focusedIndex,
        count,
        (focusedLink.textContent || '').trim()
      );
    }
  }

  function moveRecentlyViewedFocus(event) {
    var target = event.target;
    if (!target || typeof target.closest !== 'function') return false;
    var section = target.closest('recently-viewed-section');
    if (!section || section.hidden) return false;

    var links = getRecentlyViewedLinks(section);
    if (!links.length) return false;

    var currentIndex = Array.prototype.indexOf.call(links, document.activeElement);
    var nextIndex = currentIndex;

    if (event.key === 'Home') {
      nextIndex = 0;
    } else if (event.key === 'End') {
      nextIndex = links.length - 1;
    } else if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      if (currentIndex === -1) return false;
      nextIndex = Math.min(currentIndex + 1, links.length - 1);
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      if (currentIndex === -1) return false;
      nextIndex = Math.max(currentIndex - 1, 0);
    } else {
      return false;
    }

    event.preventDefault();
    links[nextIndex].focus();
    var slide = links[nextIndex].closest('.recently-viewed__slide');
    var swiperHost = section.querySelector('.recently-viewed__swiper');
    if (swiperHost && swiperHost.swiper && typeof swiperHost.swiper.slideTo === 'function') {
      swiperHost.swiper.slideTo(nextIndex);
    } else if (slide && typeof slide.scrollIntoView === 'function') {
      slide.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
    syncRecentlyViewedAria(section, links[nextIndex]);
    return true;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleStoreFromPage, { once: true });
  } else {
    scheduleStoreFromPage();
  }

  window.addEventListener('pageshow', scheduleStoreFromPage);

  document.addEventListener(
    'click',
    function (event) {
      storeFromProductLink(getProductLink(event));
    },
    true
  );

  document.addEventListener(
    'keydown',
    function (event) {
      if (event.defaultPrevented || event.repeat) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (moveRecentlyViewedFocus(event)) return;
      if (event.key === 'Enter') {
        storeFromProductLink(getProductLink(event));
        return;
      }
      if (event.key !== ' ') return;
      var spaceTarget = event.target;
      if (spaceTarget && spaceTarget.closest && spaceTarget.closest('button')) {
        storeFromProductLink(getProductLink(event));
      }
    },
    true
  );

  document.addEventListener(
    'focusin',
    function (event) {
      var target = event.target;
      if (!target || typeof target.closest !== 'function') return;
      var section = target.closest('recently-viewed-section');
      if (!section || section.hidden) return;
      var link = section.contains(target) ? getProductLink(event) : null;
      syncRecentlyViewedAria(section, link && section.contains(link) ? link : null);
    },
    true
  );
})();
